/**
 * Types for the Broadcasts feature (NEWS / PROMO push notifications via iv3api).
 *
 * Mirrors the payload contract in `iv3api/docs/NOTIFICATION_SYSTEM.md`. Keep
 * route + event constants in `src/lib/notificationRoutes.ts` in sync with the
 * backend tables on changes.
 */
import type { Timestamp } from "firebase/firestore";

/** Target environment the broadcast is sent to. */
export type BroadcastEnv = "local" | "sandbox" | "staging" | "preprod" | "prod";

export interface BroadcastEnvInfo {
  value: BroadcastEnv;
  label: string;
  baseUrl: string;
  /** Visual cue (amber for prod, etc.) — used by the env picker. */
  tone: "neutral" | "warn" | "danger";
}

/** Only the two broadcast-scope types are exposed in v1. */
export type BroadcastType = "NEWS" | "PROMOTIONAL";

/** Full proto enum value the iv3api endpoint expects. */
export const BROADCAST_TYPE_TO_PROTO: Record<BroadcastType, string> = {
  NEWS: "MOBILE_NOTIFICATION_TYPE_NEWS",
  PROMOTIONAL: "MOBILE_NOTIFICATION_TYPE_PROMOTIONAL",
};

/** Tap behavior. */
export type DataPayloadKind = "NAVIGATION" | "URL_LAUNCH";

/** In-app navigation payload — `target` is a route name (see notificationRoutes.ts). */
export interface NavigationPayload {
  type: "NAVIGATION";
  target: string;
  event?: string;
  params?: Record<string, unknown>;
}

/** External URL payload — `target` is a full https URL. */
export interface UrlLaunchPayload {
  type: "URL_LAUNCH";
  target: string;
}

export type DataPayload = NavigationPayload | UrlLaunchPayload;

/** Composer form state — what the UI binds to. */
export interface BroadcastFormState {
  env: BroadcastEnv;
  type: BroadcastType;
  title: string;
  body: string;
  tapKind: "none" | DataPayloadKind;
  navTarget: string;
  navEvent: string;
  navParamsText: string;
  url: string;
}

/**
 * Lifecycle status persisted on `notificationHistory/{id}`:
 *  - `draft`     — saved by an operator but not yet sent. Editable from the
 *                  composer; created via a direct Firestore write (rules-gated
 *                  to operators with `MANAGE_BROADCASTS`).
 *  - `published` — successfully sent through the iv3api endpoint. Written
 *                  directly by the client after the send succeeds (rules allow
 *                  managers to create `published` rows; they are immutable
 *                  afterwards — no update rule matches `status == 'published'`).
 *
 * Failed sends are NOT persisted — `callSendBroadcast` throws and the dashboard
 * surfaces the error inline.
 */
export type BroadcastStatus = "published" | "draft";

/** Stored row — written client-side by `callSendBroadcast` on a successful send. */
export interface NotificationHistoryDoc {
  env: BroadcastEnv;
  type: BroadcastType;
  title: string;
  body: string;
  dataPayload: DataPayload | null;
  status: BroadcastStatus;
  /** iv3api response `id` (UUID) when the send succeeded. */
  apiNotificationId: string | null;
  actor: {
    uid: string;
    email: string;
    displayName?: string | null;
  };
  createdAt: Timestamp;
}

/** History list item returned to the UI (id + flattened doc). */
export type NotificationHistoryListItem = NotificationHistoryDoc & {
  id: string;
};

/** Request shape passed to `callSendBroadcast`. */
export interface SendBroadcastRequest {
  env: BroadcastEnv;
  token: string;
  type: BroadcastType;
  title: string;
  body: string;
  dataPayload: DataPayload | null;
}

/** Response shape returned by `callSendBroadcast`. */
export interface SendBroadcastResponse {
  historyId: string;
  status: BroadcastStatus;
  apiNotificationId: string | null;
}
